/**
 * Sprint IA-2 · #18 · Widget de chat público del marketplace.
 * Sprint IA-3 · #20 · Handoff IA → reserva (suggested_action).
 *
 * Burbuja flotante abajo-izquierda. Inyectable en PublicBooking. Mantiene la
 * sesión en localStorage para que sobreviva a refresh.
 *
 * Si la IA detecta intención de reserva, devuelve `suggested_action`:
 *   { type: "select_service", service_id, service_name, service_price }
 * El widget muestra un CTA "Reservar <nombre> →" que cierra el panel,
 * llama al callback `onSelectService(service)` proporcionado por el padre
 * y hace scroll al stepper.
 */
import { useEffect, useRef, useState } from "react";
import { api, fmtEur } from "../lib/api";
import { ChatCircleDots, X, PaperPlaneTilt, Sparkle, ArrowRight } from "@phosphor-icons/react";

function getOrCreateSession(slug) {
  const key = `ag-chat-session-${slug}`;
  try {
    let s = localStorage.getItem(key);
    if (!s) {
      s = crypto.randomUUID();
      localStorage.setItem(key, s);
    }
    return s;
  } catch {
    // Safari privado / storage bloqueado → sesión efímera por carga
    return crypto.randomUUID();
  }
}

export function PublicChatWidget({ orgSlug, orgName, onSelectService, controlledByLauncher = false }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = orgSlug ? getOrCreateSession(orgSlug) : null;
  const scrollRef = useRef(null);

  // Sprint UNIFY · #21: si está montado por el launcher, escucha el evento
  // global para abrirse desde el picker.
  useEffect(() => {
    if (!controlledByLauncher) return;
    const handler = () => setOpen(true);
    window.addEventListener("ag:open-ai-chat", handler);
    return () => window.removeEventListener("ag:open-ai-chat", handler);
  }, [controlledByLauncher]);

  useEffect(() => {
    if (!orgSlug || !open || messages.length > 0) return;
    api.get(`/ai/chat/public/${orgSlug}/${sessionId}`)
      .then(({ data }) => setMessages(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [open, orgSlug, sessionId, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy || !orgSlug) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text, created_at: new Date().toISOString() }]);
    setInput("");
    try {
      const { data } = await api.post("/ai/chat/public", { org_slug: orgSlug, session_id: sessionId, message: text });
      setMessages((m) => [...m, {
        role: "assistant",
        text: data.reply,
        suggested_action: data.suggested_action || null,
        created_at: new Date().toISOString(),
      }]);
    } catch (err) {
      const status = err?.response?.status;
      const detail = status === 429
        ? "Has enviado muchos mensajes seguidos. Espera unos segundos."
        : "No puedo responder ahora. Llámanos o pulsa 'Reservar'.";
      setMessages((m) => [...m, { role: "assistant", text: detail, created_at: new Date().toISOString() }]);
    } finally { setBusy(false); }
  };

  const handleBookSuggestion = (action) => {
    if (!action || action.type !== "select_service" || !onSelectService) return;
    setOpen(false);
    onSelectService({
      id: action.service_id,
      name: action.service_name,
      price: action.service_price,
      suggested_slot_iso: action.suggested_slot_iso || null,
      suggested_date: action.suggested_date || null,
    });
  };

  const formatSuggestedSlot = (action) => {
    if (!action?.suggested_slot_iso) return null;
    try {
      const dt = new Date(action.suggested_slot_iso);
      return dt.toLocaleString("es-ES", {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return null; }
  };

  if (!orgSlug) return null;

  return (
    <>
      {!open && !controlledByLauncher && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-4 left-4 z-40 w-14 h-14 rounded-full bg-[var(--ag-gold)] text-white shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
          data-testid="public-chat-fab">
          <ChatCircleDots size={26} weight="fill" />
          <span className="absolute -top-1 -right-1 bg-amber-400 text-[#1A1A1A] text-[10px] font-bold rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
            <Sparkle size={9} weight="fill" /> IA
          </span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-4 left-4 z-50 w-[calc(100vw-32px)] sm:w-80 max-h-[70vh] bg-white border border-gray-200 rounded-sm shadow-xl flex flex-col"
             data-testid="public-chat-panel">
          <div className="bg-[var(--ag-gold)] text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] opacity-80">Asistente · IA</div>
              <div className="font-medium text-sm truncate">{orgName || "Centro"}</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[var(--ag-text)]/80 hover:text-white" data-testid="public-chat-close">
              <X size={18} weight="bold" />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50" style={{ minHeight: 200 }}>
            {messages.length === 0 && (
              <div className="text-xs text-gray-500 text-center py-6">
                Pregúntame sobre servicios, precios u horarios. <br /> Estoy aquí para ayudarte ✨
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className="space-y-1.5">
                <div className={`max-w-[85%] ${m.role === "user" ? "ml-auto bg-[var(--ag-gold)] text-white" : "mr-auto bg-white border border-gray-200 text-gray-800"} px-3 py-2 rounded-sm text-sm`}>
                  {m.text}
                </div>
                {m.role === "assistant" && m.suggested_action?.type === "select_service" && (
                  <div className="space-y-1.5">
                    <button
                      onClick={() => handleBookSuggestion(m.suggested_action)}
                      className="mr-auto flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-[#1A1A1A] text-xs font-semibold px-3 py-2 rounded-sm shadow-sm transition-colors"
                      data-testid="public-chat-book-suggestion"
                    >
                      <Sparkle size={12} weight="fill" />
                      <div className="flex flex-col items-start min-w-0 max-w-[200px]">
                        <span className="truncate">Reservar {m.suggested_action.service_name}</span>
                        {formatSuggestedSlot(m.suggested_action) && (
                          <span className="text-[10px] font-normal opacity-80" data-testid="public-chat-book-slot">
                            {formatSuggestedSlot(m.suggested_action)}
                          </span>
                        )}
                      </div>
                      {m.suggested_action.service_price ? (
                        <span className="font-bold tabular-nums">· {fmtEur(m.suggested_action.service_price)}</span>
                      ) : null}
                      <ArrowRight size={12} weight="bold" />
                    </button>
                    {/* Sprint S-6 · multi-slot chips */}
                    {(m.suggested_action.suggested_slots || []).length > 1 && (
                      <div className="flex flex-wrap gap-1.5 max-w-[260px]" data-testid="public-chat-slots">
                        {(m.suggested_action.suggested_slots || []).slice(0, 3).map((s, j) => {
                          const dt = new Date(s);
                          const label = dt.toLocaleString("es-ES", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
                          return (
                            <button key={j}
                              onClick={() => handleBookSuggestion({ ...m.suggested_action, suggested_slot_iso: s, suggested_date: s.slice(0, 10) })}
                              className="inline-flex items-center gap-1 bg-white border border-amber-300 hover:bg-amber-50 text-[#1A1A1A] text-[10px] font-semibold px-2 py-1 rounded-sm transition-colors"
                              data-testid={`public-chat-slot-${j}`}
                              title={`Reservar ${m.suggested_action.service_name} el ${label}`}
                            >
                              <Sparkle size={9} weight="fill" /> {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="max-w-[85%] mr-auto bg-white border border-gray-200 text-gray-500 px-3 py-2 rounded-sm text-sm italic">
                Pensando…
              </div>
            )}
          </div>
          <div className="border-t border-gray-200 p-2 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Escribe tu pregunta…"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-sm focus:outline-none focus:border-[var(--ag-gold)]"
              disabled={busy} data-testid="public-chat-input" />
            <button onClick={send} disabled={busy || !input.trim()}
              className="bg-[var(--ag-gold)] text-white px-3 rounded-sm hover:bg-[var(--ag-gold)]/90 disabled:opacity-50"
              data-testid="public-chat-send">
              <PaperPlaneTilt size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
